/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import BrandUsersContext from './BrandUsersContext';
import initialBrandUsersState from './initialBrandUsersState';
import brandUsersReducer from 'contexts/brandUsers/reducers/brandUsers.reducer';
import * as action from 'contexts/brandUsers/reducers/brandUsers.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IBrandUser,
  IcreateUpdateBrandUserProps,
  IupdateBrandUserActionProps,
} from 'interfaces/brandUsers.interface';
import useContextUser from 'hooks/contexts/useContextUser';
import useContextSuperAdmin from 'hooks/contexts/useContextSuperAdmin';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function BrandUsersProvider(props: ChildrenProps) {
  const [state, dispatch] = useReducer(brandUsersReducer, initialBrandUsersState);
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();
  const { addUserClient } = useContextSuperAdmin();

  const getBrandUsers = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !dbUser?.role || !company?.id) return false;
    return await action.getBrandUsersAction({
      dispatch,
      clientId: company.id,
      userRole: dbUser.role,
      token,
      translate,
    });
  }, [company?.id, dbUser?.role]);

  const selectBrandUser = useCallback((user: IBrandUser | null) => {
    action.selectBrandUserAction({ dispatch, user, translate });
  }, []);

  const createBrandUser = useCallback(
    async (newBrandUser: IcreateUpdateBrandUserProps) => {
      const token = await getAccessTokenSilently();
      if (!company?.id) return false;
      return await action.createBrandUserAction({
        dispatch,
        clientId: company.id,
        newBrandUser,
        token,
        translate,
        addUserClient,
      });
    },
    [company?.id, addUserClient]
  );

  const updateBrandUser = useCallback(
    async ({ user, updateRole, prevRole }: IupdateBrandUserActionProps) => {
      if (!state.brandUsersSelected) return false;
      const token = await getAccessTokenSilently();
      return await action.updateBrandUserAction({
        dispatch,
        user,
        userId: state.brandUsersSelected.key,
        updateRole,
        prevRole,
        token,
        translate,
      });
    },
    [state.brandUsersSelected]
  );

  const deleteBrandUser = useCallback(async (userId: string) => {
    const token = await getAccessTokenSilently();
    return await action.deleteBrandUserAction({ dispatch, userId, token, translate });
  }, []);

  const memoProvider = useMemo(
    () => ({
      ...state,
      getBrandUsers,
      selectBrandUser,
      createBrandUser,
      updateBrandUser,
      deleteBrandUser,
    }),
    [
      state,
      getBrandUsers,
      selectBrandUser,
      createBrandUser,
      updateBrandUser,
      deleteBrandUser,
    ]
  );

  return (
    <BrandUsersContext.Provider value={memoProvider}>
      {props.children}
    </BrandUsersContext.Provider>
  );
}

export default memo(BrandUsersProvider);
